/**
 * Hook pour gérer les contrats de sous-traitance d'un chantier
 * @module hooks/useContratsST
 *
 * Migré vers React Query (useQuery + useMutation)
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateFinancesChantier } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type {
  ContratSousTraitance,
  ContratSTCreate,
  ContratSTUpdate,
  ContratSTAvecTotaux,
  FactureContratST,
} from '@/types/sous-traitant';

// =============================================================================
// Types de retour du hook
// =============================================================================

export interface UseContratsSTReturn {
  /** Liste des contrats avec totaux calculés */
  contrats: ContratSTAvecTotaux[];
  /** Factures liées aux contrats (map par contrat_id) */
  facturesParContrat: Map<string, FactureContratST[]>;
  /** Chargement en cours */
  isLoading: boolean;
  /** Erreur éventuelle */
  error: Error | null;
  /** Total des montants contrats HT */
  totalContratsHT: number;
  /** Total des montants facturés HT */
  totalFactureHT: number;
  /** Total reste à facturer */
  totalResteAFacturer: number;
  /** Créer un contrat ST */
  createContratST: (data: ContratSTCreate) => Promise<ContratSousTraitance | null>;
  /** Mettre à jour un contrat ST */
  updateContratST: (id: string, data: ContratSTUpdate) => Promise<boolean>;
  /** Supprimer un contrat ST */
  deleteContratST: (id: string) => Promise<boolean>;
  /** Récupérer les contrats d'un ST spécifique */
  getContratsByST: (sousTraitantId: string) => ContratSTAvecTotaux[];
  /** Récupérer les factures d'un contrat spécifique */
  getFacturesByContrat: (contratId: string) => FactureContratST[];
  /** Trouver un contrat existant pour un ST sur ce chantier */
  findContratForST: (sousTraitantId: string) => ContratSTAvecTotaux | null;
  /** Recharger les données */
  refetch: () => Promise<void>;
}

// =============================================================================
// Type interne pour le résultat de la query
// =============================================================================

interface ContratsQueryResult {
  contrats: ContratSTAvecTotaux[];
  facturesRecord: Record<string, FactureContratST[]>;
}

// =============================================================================
// Hook useContratsST
// =============================================================================

/**
 * Hook pour gérer les contrats de sous-traitance d'un chantier
 * @param chantierId - ID du chantier
 */
export function useContratsST(chantierId: string | null): UseContratsSTReturn {
  const { toast } = useToast();
  const { entrepriseId } = useSubscription();
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Query : charger les contrats ST du chantier (avec factures liées)
  // -------------------------------------------------------------------------
  const {
    data: queryData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<ContratsQueryResult>({
    queryKey: queryKeys.finances.contratsST(entrepriseId!, chantierId!),
    queryFn: async (): Promise<ContratsQueryResult> => {
      let contratsData: ContratSTAvecTotaux[] = [];

      // Forcer le fallback car la RPC semble défaillante
      // TODO: Investiguer et réparer la fonction RPC get_chantier_contrats_st
      console.warn('[useContratsST] RPC non disponible, utilisation du fallback');

      // Charger les contrats avec jointure sur sous_traitants
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('contrats_sous_traitance')
        .select(`
          id,
          sous_traitant_id,
          description_travaux,
          montant_contrat_ht,
          statut,
          date_debut_prevue,
          date_fin_prevue,
          sous_traitants!inner(nom, specialite)
        `)
        .eq('chantier_id', chantierId!);

      if (fallbackError) throw fallbackError;

      // Charger les frais liés pour calculer les totaux
      const contratIds = (fallbackData || []).map((c: any) => c.id);
      const fraisParContrat: Record<string, { total: number; count: number }> = {};

      if (contratIds.length > 0) {
        const { data: fraisData } = await supabase
          .from('frais_chantier')
          .select('contrat_st_id, montant_ht')
          .in('contrat_st_id', contratIds);

        // Agréger les frais par contrat
        (fraisData || []).forEach((f: any) => {
          if (!fraisParContrat[f.contrat_st_id]) {
            fraisParContrat[f.contrat_st_id] = { total: 0, count: 0 };
          }
          fraisParContrat[f.contrat_st_id].total += f.montant_ht || 0;
          fraisParContrat[f.contrat_st_id].count += 1;
        });
      }

      // Transformer en ContratSTAvecTotaux
      contratsData = (fallbackData || []).map((c: any) => {
        const facture = fraisParContrat[c.id] || { total: 0, count: 0 };
        const montantContrat = c.montant_contrat_ht || 0;
        const montantFacture = facture.total;
        return {
          contrat_id: c.id,
          sous_traitant_id: c.sous_traitant_id,
          sous_traitant_nom: c.sous_traitants?.nom || 'Inconnu',
          sous_traitant_specialite: c.sous_traitants?.specialite || null,
          description_travaux: c.description_travaux,
          montant_contrat_ht: montantContrat,
          montant_facture_ht: montantFacture,
          reste_a_facturer: Math.max(montantContrat - montantFacture, 0),
          pourcentage_facture: montantContrat > 0 ? Math.round((montantFacture / montantContrat) * 1000) / 10 : 0,
          statut: c.statut,
          date_debut_prevue: c.date_debut_prevue,
          date_fin_prevue: c.date_fin_prevue,
          nb_factures: facture.count,
        };
      });

      // Charger les factures liées à ces contrats
      let facturesRecord: Record<string, FactureContratST[]> = {};

      if (contratsData.length > 0) {
        const ids = contratsData.map(c => c.contrat_id);
        const { data: facturesData, error: facturesError } = await supabase
          .from('frais_chantier')
          .select('id, contrat_st_id, date_frais, montant_ht, description, created_at')
          .in('contrat_st_id', ids)
          .order('date_frais', { ascending: false });

        if (!facturesError && facturesData) {
          // Grouper les factures par contrat_id (en Record, pas Map)
          facturesData.forEach((f) => {
            const contratId = f.contrat_st_id as string;
            if (!facturesRecord[contratId]) {
              facturesRecord[contratId] = [];
            }
            facturesRecord[contratId].push({
              id: f.id,
              contrat_st_id: contratId,
              date_facture: f.date_frais,
              montant_ht: f.montant_ht,
              description: f.description,
              type_facture_st: null,
              numero_facture: null,
              created_at: f.created_at,
            });
          });
        }
      }

      return { contrats: contratsData, facturesRecord };
    },
    enabled: !!entrepriseId && !!chantierId,
  });

  // -------------------------------------------------------------------------
  // Données dérivées
  // -------------------------------------------------------------------------
  const contrats = queryData?.contrats ?? [];

  // Reconstruire Map depuis Record (React Query gère bien les objets plats, pas les Map)
  const facturesParContrat = useMemo(() => {
    if (!queryData?.facturesRecord) return new Map<string, FactureContratST[]>();
    return new Map(Object.entries(queryData.facturesRecord));
  }, [queryData?.facturesRecord]);

  // -------------------------------------------------------------------------
  // Calculs agrégés
  // -------------------------------------------------------------------------
  const totalContratsHT = useMemo(() => {
    return contrats.reduce((sum, c) => sum + (c.montant_contrat_ht || 0), 0);
  }, [contrats]);

  const totalFactureHT = useMemo(() => {
    return contrats.reduce((sum, c) => sum + (c.montant_facture_ht || 0), 0);
  }, [contrats]);

  const totalResteAFacturer = useMemo(() => {
    return contrats.reduce((sum, c) => sum + (c.reste_a_facturer || 0), 0);
  }, [contrats]);

  // -------------------------------------------------------------------------
  // Mutation : créer un contrat ST
  // -------------------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: async (data: ContratSTCreate) => {
      const { data: newContrat, error: insertError } = await supabase
        .from('contrats_sous_traitance')
        .insert({
          ...data,
          statut: data.statut || 'en_cours',
        })
        .select()
        .single();

      if (insertError) {
        // Gérer l'erreur de contrainte unique (ST déjà sur ce chantier)
        if (insertError.code === '23505') {
          toast({
            title: 'Contrat existant',
            description:
              'Ce sous-traitant a déjà un contrat sur ce chantier. Modifiez le contrat existant.',
            variant: 'destructive',
          });
          return null;
        }
        throw insertError;
      }

      return newContrat as ContratSousTraitance;
    },
    onSuccess: (result) => {
      if (result) {
        toast({
          title: 'Contrat créé',
          description: 'Le contrat de sous-traitance a été créé',
        });
        invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
      }
    },
    onError: (err) => {
      console.error('[useContratsST] Erreur création:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le contrat',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Mutation : mettre à jour un contrat ST
  // -------------------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ContratSTUpdate }) => {
      const { error: updateError } = await supabase
        .from('contrats_sous_traitance')
        .update(data)
        .eq('id', id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast({
        title: 'Contrat mis à jour',
        description: 'Les modifications ont été enregistrées',
      });
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err) => {
      console.error('[useContratsST] Erreur modification:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le contrat',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Mutation : supprimer un contrat ST
  // -------------------------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: deleteError } = await supabase
        .from('contrats_sous_traitance')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      toast({
        title: 'Contrat supprimé',
        description: 'Le contrat a été supprimé. Les factures liées ne sont plus associées.',
      });
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err) => {
      console.error('[useContratsST] Erreur suppression:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le contrat',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Wrappers préservant les signatures de retour
  // -------------------------------------------------------------------------
  const createContratST = async (data: ContratSTCreate): Promise<ContratSousTraitance | null> => {
    try {
      return await createMutation.mutateAsync(data);
    } catch {
      return null;
    }
  };

  const updateContratST = async (id: string, data: ContratSTUpdate): Promise<boolean> => {
    try {
      await updateMutation.mutateAsync({ id, data });
      return true;
    } catch {
      return false;
    }
  };

  const deleteContratST = async (id: string): Promise<boolean> => {
    try {
      await deleteMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  };

  // -------------------------------------------------------------------------
  // Fonctions de lookup (depuis le cache local)
  // -------------------------------------------------------------------------
  const getContratsByST = useCallback(
    (sousTraitantId: string): ContratSTAvecTotaux[] => {
      return contrats.filter((c) => c.sous_traitant_id === sousTraitantId);
    },
    [contrats]
  );

  const getFacturesByContrat = useCallback(
    (contratId: string): FactureContratST[] => {
      return facturesParContrat.get(contratId) || [];
    },
    [facturesParContrat]
  );

  const findContratForST = useCallback(
    (sousTraitantId: string): ContratSTAvecTotaux | null => {
      return contrats.find((c) => c.sous_traitant_id === sousTraitantId) || null;
    },
    [contrats]
  );

  // -------------------------------------------------------------------------
  // Retour du hook (API publique identique)
  // -------------------------------------------------------------------------
  return {
    contrats,
    facturesParContrat,
    isLoading,
    error: queryError ?? null,
    totalContratsHT,
    totalFactureHT,
    totalResteAFacturer,
    createContratST,
    updateContratST,
    deleteContratST,
    getContratsByST,
    getFacturesByContrat,
    findContratForST,
    refetch: async () => { await refetch(); },
  };
}
